import { type DrawerProps, type DrawerRefs } from './types';

interface UpdateDrawerStylesParams {
    node: HTMLDivElement;
    /**
     * Смещение дровера на текущий момент
     */
    position: number;
    /**
     * Смещение дровера на момент перед началом свайпа
     */
    defaultPosition: number;
    contentNode: HTMLDivElement;
    drawerNode: HTMLDivElement;
}

const updateDrawerContentSize = ({
    node,
    position,
    defaultPosition = 0,
    contentNode,
    drawerNode,
}: UpdateDrawerStylesParams) => {
    const nodeContentTopOffset = contentNode.getBoundingClientRect().top - node.getBoundingClientRect().top;

    /**
     * Если дровер находится или возвращается в исходное положение (position === defaultPosition) И
     * Если текущее смещение дровера ненулевое или если нулевое, но размер контента превышает размер дровера ( есть возможность скрола)
     */
    const isInitialPosition = position === defaultPosition;
    const isContentMore = node.clientHeight - nodeContentTopOffset < contentNode.scrollHeight;
    const isScrollPossible = position === 0 && isContentMore;

    if (isInitialPosition && (position !== 0 || isScrollPossible)) {
        /**
         * Добавляем отступ снизу контента, чтобы нивелировать изменение размеров контента относительно текущего смещения дровера
         * Этот отступ также используется в DrawerButton для фиксации кнопки при скроле контента
         * */
        drawerNode.style.setProperty('--drawer-content-bottom-offset', `${position}px`);

        return;
    }

    /**
     *  При нулевом смещении дровера обнуляем значение --drawer-content-bottom-offset
     * */
    if (position === 0) {
        drawerNode.style.setProperty('--drawer-content-bottom-offset', `0px`);

        return;
    }
};

const updateDrawerPosition = ({
    position,
    defaultPosition = 0,
    drawerNode,
}: Omit<UpdateDrawerStylesParams, 'contentNode'>) => {
    if (position === defaultPosition) {
        drawerNode.style.setProperty(
            '--drawer-swipe-transition',
            `transform 0.5s cubic-bezier(0.32, 0.72, 0, 1), border-radius 0.3s`
        );
    } else {
        drawerNode.style.setProperty('--drawer-swipe-transition', 'none');
    }

    drawerNode.style.setProperty('--drawer-offset', `${position}px`);
};

const updateDrawerMaxSize = ({
    node,
    maxSize,
}: Pick<UpdateDrawerStylesParams, 'node'> & Pick<DrawerProps, 'maxSize'>) => {
    if (!maxSize) {
        node.style.removeProperty('max-height');

        return;
    }

    node.style.maxHeight = maxSize;
};

const updateDrawerBorderStyles = ({ position, node }: Pick<UpdateDrawerStylesParams, 'position' | 'node'>) => {
    if (position > 0) {
        node.style.setProperty('--drawer-border-radius', '24px');
    } else {
        node.style.removeProperty('--drawer-border-radius');
    }
};

export const updateDrawerStyles = ({
    containerRef,
    contentRef,
    drawerRef,
    position,
    maxSize,
    defaultPosition,
}: Pick<UpdateDrawerStylesParams, 'position' | 'defaultPosition'> &
    Pick<DrawerProps, 'maxSize'> &
    Pick<DrawerRefs, 'containerRef' | 'contentRef' | 'drawerRef'>) => {
    if (!containerRef.current || !contentRef.current || !drawerRef.current) {
        return;
    }

    updateDrawerMaxSize({ node: containerRef.current, maxSize });
    updateDrawerPosition({
        node: containerRef.current,
        position,
        defaultPosition,
        drawerNode: drawerRef.current,
    });
    updateDrawerContentSize({
        node: containerRef.current,
        position,
        defaultPosition,
        contentNode: contentRef.current,
        drawerNode: drawerRef.current,
    });
    updateDrawerBorderStyles({ node: containerRef.current, position });
};
